import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "../../../styles/panel/user/ManageOrders.css";
import http from "../../../api/http";
import moment from "jalali-moment";
import toast, { Toaster } from "react-hot-toast";
import { swalConfirm, swalError, swalWarning } from "../../../utils/swalAlert";
import { formatToman } from "../../../utils/stringUtil";

/* ============ داده‌ی وضعیت‌ها ============ */
export const ORDER_STATUS_MAP = {
  pending: { label: "در حال بررسی", cls: "pending" },
  approved: { label: "تایید شده", cls: "approved" },
  preparing: { label: "آماده‌سازی", cls: "preparing" },
  shipped: { label: "ارسال شده", cls: "shipped" },
  delivered: { label: "تحویل داده شده", cls: "delivered" },
  cancelled: { label: "لغو شده", cls: "cancelled" },
};
export const PRODUCT_STATUS_MAP = {
  pending: { label: "در حال بررسی", cls: "pending" },
  approved: { label: "تایید شده", cls: "approved" },
  rejected: { label: "لغو شده", cls: "rejected" },
};
const FILTERS = [
  { id: "all", label: "همه" },
  ...Object.entries(ORDER_STATUS_MAP).map(([id, v]) => ({
    id,
    label: v.label,
  })),
];

export const ORDER_STATUS_KEYS = Object.keys(ORDER_STATUS_MAP);
export const PRODUCT_STATUS_KEYS = Object.keys(PRODUCT_STATUS_MAP);

/* ============ وضعیت‌های مجاز بعدی برای هر وضعیت فعلی ============ */
const STATUS_TRANSITIONS = {
  pending: ["approved", "cancelled"],
  cancelled: ["approved"],
  approved: ["preparing", "shipped", "delivered", "cancelled"],
  preparing: ["shipped", "delivered", "cancelled"],
  shipped: ["delivered", "cancelled"],
  delivered: [],
};

const getStatusOptions = (currentStatus) =>
  STATUS_TRANSITIONS[currentStatus] ||
  ORDER_STATUS_KEYS.filter((k) => k !== currentStatus);

const PAGE_SIZE = 6;

export default function ManageOrders() {
  const navigate = useNavigate();

  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("all");
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [previewImage, setPreviewImage] = useState(null);

  const loadOrders = (pageNumber = 1, searchQuery = "") => {
    setLoading(true);
    http
      .get("/orders/user/orders/", {
        params: { page: pageNumber, search: searchQuery, page_size: PAGE_SIZE },
      })
      .then((res) => {
        const results = res.data.results?.results || res.data.results || [];
        setOrders(results);
        setTotalCount(res.data.count || 0);
      })
      .catch((err) => {
        toast.error(
          err?.response?.data?.error || "خطایی در انجام عملیات رخ داد.",
        );
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadOrders(page, search);
  }, [page]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (page === 1) {
        loadOrders(1, search);
      } else {
        setPage(1);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  const counts = useMemo(() => {
    const c = { all: totalCount };
    ORDER_STATUS_KEYS.forEach((k) => (c[k] = 0));
    orders.forEach((o) => (c[o.status] = (c[o.status] || 0) + 1));
    return c;
  }, [orders, totalCount]);

  const filtered = useMemo(() => {
    if (filter === "all") return orders;
    return orders.filter((o) => o.status === filter);
  }, [orders, filter]);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const pageSafe = Math.min(page, totalPages);
  const paged = filtered;

  const changeStatus = async (id, newStatus) => {
    const targetOrder = orders.find((o) => o.id === id);
    console.log(targetOrder);
    // اگر فروشگاه هنوز شماره کارت ثبت نکرده، اجازه‌ی تایید سفارش داده نشود
    if (
      newStatus === "approved" &&
      targetOrder &&
      !targetOrder.store_card_number
    ) {
      const isConfirm = await swalConfirm({
        title: "خطا در دریافت اطلاعات",
        text: " برای تایید سفارش، فروشگاه باید ابتدا شماره کارت خود را ثبت کند.",
        confirmText: "ثبت شماره کارت",
      });

      if (isConfirm) {
        navigate(`/panel/user/manage-shops/${targetOrder.store_id}/card`);
      }

      // در هر دو حالت (تایید یا انصراف)، دیگر ادامه‌ی تابع اجرا نشود
      return;
    }

    const prevOrders = orders;
    setOrders((prev) =>
      prev.map((o) => (o.id === id ? { ...o, status: newStatus } : o)),
    );

    http
      .patch(`/orders/update-status/${id}/`, { status: newStatus })
      .then(() => {
        toast.success("وضعیت با موفقیت تغییر کرد");
      })
      .catch((err) => {
        setOrders(prevOrders);
        toast.error(
          err?.response?.data?.error || "خطایی در انجام عملیات رخ داد.",
        );
      });
  };

  const pageNumbers = useMemo(() => {
    const nums = [];
    for (let n = 1; n <= totalPages; n++) nums.push(n);
    return nums;
  }, [totalPages]);

  const openDetails = (order) => {
    navigate(`${order.id}`);
  };

  return (
    <div className="orders-page">
      <Toaster
        position="top-center"
        toastOptions={{
          duration: 3000,
          style: {
            fontFamily: "inherit",
            direction: "rtl",
            fontSize: "13.5px",
          },
        }}
      />
      <div className="orders-header">
        <div>
          <h1>مدیریت سفارش‌ها</h1>
          <p className="orders-sub">
            پیگیری و به‌روزرسانی وضعیت سفارش‌های ثبت‌شده در فروشگاه‌ها
          </p>
        </div>
      </div>

      {/* ---- جستجو ---- */}
      <div className="orders-search">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="11" cy="11" r="7" />
          <path d="M21 21l-4.3-4.3" />
        </svg>
        <input
          type="text"
          inputMode="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="جستجو بر اساس کد سفارش، نام مشتری یا شماره موبایل"
        />
        {search && (
          <button
            className="search-clear"
            onClick={() => setSearch("")}
            aria-label="پاک کردن جستجو"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* ---- فیلترها ---- */}
      <div className="orders-filters">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            className={`filter-pill${filter === f.id ? " active" : ""}`}
            onClick={() => setFilter(f.id)}
          >
            {f.label}
            <span className="filter-count">{counts[f.id] ?? 0}</span>
          </button>
        ))}
      </div>

      {/* ---- جدول ---- */}
      <div className="orders-table-card">
        <div className="orders-table-scroll">
          <table className="orders-table">
            <thead>
              <tr>
                <th>کد</th>
                <th>فروشگاه</th>
                <th>مشتری</th>
                <th>تماس</th>
                <th>وضعیت</th>
                <th>تاریخ</th>
                <th>مبلغ کل سفارش</th>
                <th>تغییر وضعیت</th>
                <th>فیش واریزی</th>

                <th>جزئیات</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr className="empty-row">
                  <td colSpan={8}>در حال دریافت سفارش‌ها...</td>
                </tr>
              )}
              {!loading && paged.length === 0 && (
                <tr className="empty-row">
                  <td colSpan={8}>
                    {search.trim()
                      ? "سفارشی مطابق با جستجوی شما پیدا نشد."
                      : "سفارشی با این وضعیت یافت نشد."}
                  </td>
                </tr>
              )}
              {!loading &&
                paged.map((o) => (
                  <tr key={o.id}>
                    <td data-label="کد" className="cell-code">
                      {o.order_code}
                    </td>
                    <td data-label="فروشگاه">{o.store_name}</td>
                    <td data-label="مشتری">{o.customer_name}</td>
                    <td data-label="تماس" className="cell-phone" dir="ltr">
                      {o.customer_phone}
                    </td>
                    <td data-label="وضعیت">
                      <span
                        className={`status-badge ${ORDER_STATUS_MAP[o.status]?.cls || ""}`}
                      >
                        {ORDER_STATUS_MAP[o.status]?.label || o.status}
                      </span>
                    </td>
                    <td data-label="تاریخ" className="cell-date">
                      {moment(o.created_at).format("jYYYY/jMM/jDD")}
                    </td>
                    <td data-label="مبلغ کل سفارش">
                      {formatToman(Number(o.total_price))}
                    </td>
                    <td data-label="تغییر وضعیت" className="cell-status-change">
                      <select
                        className="status-select"
                        value={o.status}
                        onChange={(e) => changeStatus(o.id, e.target.value)}
                      >
                        <option value={o.status} disabled>
                          {ORDER_STATUS_MAP[o.status]?.label || o.status}
                        </option>
                        {getStatusOptions(o.status).map((k) => (
                          <option key={k} value={k}>
                            {ORDER_STATUS_MAP[k].label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td data-label="فیش واریزی" className="cell-receipt">
                      {o.receipt_image ? (
                        <button
                          type="button"
                          className="receipt-thumb-btn"
                          onClick={() => setPreviewImage(o.receipt_image)}
                          aria-label="مشاهده فیش واریزی"
                        >
                          <img src={o.receipt_image} alt="فیش واریزی" />
                        </button>
                      ) : (
                        <span className="receipt-empty">—</span>
                      )}
                    </td>
                    <td data-label="جزئیات" className="cell-details">
                      <button
                        className="details-btn"
                        onClick={() => openDetails(o)}
                        aria-label="مشاهده جزئیات"
                      >
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="details-btn-icon"
                        >
                          <path d="M1.5 12S5 5 12 5s10.5 7 10.5 7-3.5 7-10.5 7S1.5 12 1.5 12z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        {/* ---- صفحه‌بندی ---- */}
        {totalCount > 0 && (
          <div className="orders-pagination">
            <span className="pagination-info">
              {totalCount.toLocaleString("fa-IR")} سفارش — صفحه{" "}
              {pageSafe.toLocaleString("fa-IR")} از{" "}
              {totalPages.toLocaleString("fa-IR")}
            </span>
            <div className="pagination-controls">
              <button
                className="page-btn"
                disabled={pageSafe === 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                aria-label="صفحه قبل"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M15 6l-6 6 6 6" />
                </svg>
              </button>
              {pageNumbers.map((n) => (
                <button
                  key={n}
                  className={`page-btn number${n === pageSafe ? " active" : ""}`}
                  onClick={() => setPage(n)}
                >
                  {n.toLocaleString("fa-IR")}
                </button>
              ))}
              <button
                className="page-btn"
                disabled={pageSafe === totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                aria-label="صفحه بعد"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M9 6l6 6-6 6" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ---- مدال نمایش فیش واریزی ---- */}
      {previewImage && (
        <div
          className="receipt-modal-backdrop"
          onClick={() => setPreviewImage(null)}
        >
          <div className="receipt-modal" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="receipt-modal-close"
              onClick={() => setPreviewImage(null)}
              aria-label="بستن"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
            <img src={previewImage} alt="فیش واریزی" />
          </div>
        </div>
      )}
    </div>
  );
}
